import { Copy } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";

interface CopyableValueProps {
  value: string | null | undefined;
  /** Shown instead of the value when there is none. */
  placeholder?: string;
  className?: string;
  /** Show the whole value on one line instead of an ellipsis. Use for columns
   *  that only ever hold real domains/URLs (≤ ~80 chars); the cell grows. Do NOT
   *  use on the Clicks dimension column, which can hold multi-KB junk (see note). */
  noTruncate?: boolean;
}

/** Write to the clipboard and toast. The clipboard API needs a secure context
 *  (https or localhost); over plain http it throws, so the catch must speak. */
async function copyToClipboard(value: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success("Copied", { description: value });
  } catch {
    toast.error("Could not copy — the clipboard is not available here");
  }
}

/**
 * A domain or a URL that copies itself when clicked.
 *
 * It is a <button> rather than a click handler on the cell so that it carries
 * focus and reads as a control. The click is stopped from propagating because
 * the row underneath toggles the breakdown, and copying an address must not
 * also expand it — which also means this must never be rendered inside another
 * button: nested buttons are invalid HTML and the browser drops the inner one.
 *
 * One line, never wrapped: rows of different heights make the table unreadable.
 * The cell is allowed to grow instead, and only junk gets an ellipsis —
 * measured 28.09.2026, real domains top out at 80 characters while the longest
 * value in that column is 5,077, a sqlmap UNION payload captured in sub_id_4.
 * The whole value stays in the tooltip and one click away on the clipboard.
 */
export function CopyableValue({
  value,
  placeholder = "Not set",
  className,
  noTruncate = false,
}: CopyableValueProps) {
  // clicks_daily stores the literal string "Not set" rather than NULL, so both
  // spellings of "nothing here" have to be caught — otherwise the table shows a
  // plain unstyled "Not set" that looks like a real domain and invites a copy.
  if (!value || value === "Not set" || value === "Unknown") {
    return (
      <span className="italic text-muted-foreground/70">
        {value || placeholder}
      </span>
    );
  }

  const copy = (event: React.MouseEvent) => {
    event.stopPropagation();
    void copyToClipboard(value);
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={`${value}\nClick to copy`}
      className={cn(
        "group/copy inline-flex cursor-pointer items-center gap-1.5 text-left",
        noTruncate ? "whitespace-nowrap" : "min-w-0 max-w-full",
        className,
      )}
    >
      <span className={noTruncate ? undefined : "truncate"}>{value}</span>
      <Copy
        className="size-3.5 shrink-0 text-transparent transition-colors group-hover/copy:text-muted-foreground"
        aria-hidden
      />
    </button>
  );
}

/**
 * Just the copy affordance — a faint icon button — for a cell whose value is
 * already another control (a link, a drill-down, an editable cell) and so cannot
 * itself be the CopyableValue button (nested buttons are invalid HTML). Place it
 * beside that control; it stops propagation so it never fires the control or row
 * underneath. Returns null for an empty / "Not set" value, like CopyableValue.
 */
export function CopyIconButton({ value, className }: { value: string | null | undefined; className?: string }) {
  if (!value || value === "Not set" || value === "Unknown") return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void copyToClipboard(value);
      }}
      title={`Copy\n${value}`}
      aria-label="Copy"
      className={cn(
        "shrink-0 cursor-pointer text-muted-foreground/50 transition-colors hover:text-foreground",
        className,
      )}
    >
      <Copy className="size-3.5" aria-hidden />
    </button>
  );
}
